import React from "react";
import logo from "../img/enjuggernaut_logo.png";
import close from "../img/close.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import ResumeDisplay, { ResumeContents } from "./ResumeDisplay";
import ScoreCard from "./ScoreCard";
import { Questionnaire } from "../Questionnaire/Questionnaire";
import { useAuthToken } from "../AuthProvider";
import { toast } from "react-toastify";
import { User, UserScore } from "../Profile";
import { Tooltip } from "react-tooltip";
import ScoreAccordion from "./ScoreAccordion";
import { Sidebar } from "../Sidebar";
import { Drawer } from "@mui/material";
import { TemplatesDisplay } from "../Templates/TemplatesDisplay";
import useWindowDimensions from "../Account/WindowDimensions";
import tailor from "../img/tailor.svg";
import workshop from "../img/workshop.svg";

function GetWidthDivisor(width: number) {
  if (width >= 1280) {
    return 2;
  }

  if (width >= 1024) {
    return 2;
  }

  if (width >= 768) {
    return 2.2;
  }

  return 2;
}

export function Score() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resumeCredits, setResumeCredits] = React.useState(0);
  const user = useSelector((state: AppState) => state.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isTemplatesOverlayOpen, setIsTemplatesOverlayOpen] =
    React.useState<boolean>(false);
  const [isQuestionnaireOverlayOpen, setIsQuestionnaireOverlayOpen] =
    React.useState<boolean>(
      searchParams.has("questionnaire") &&
        searchParams.get("questionnaire") == "true"
    );
  const { isAuthenticated, token, isLoading, updateToken } = useAuthToken();
  const userInfo = useSelector((state: AppState) => state.user);
  const [completed, setCompleted] = React.useState<boolean>(
    searchParams.has("complete")
  );
  const [updatesText, setUpdatesText] = React.useState<string[]>([
    "Live updates from resume processing will appear here. When complete, your score breakdown will show above.",
  ]);
  const [updatesTime, setUpdatesTime] = React.useState<number[]>([0]);
  const [skillsBullets, setSkillsBullets] = React.useState<string[]>([]);
  const [numbersBullets, setNumbersBullets] = React.useState<string[]>([]);
  const [wordingBullets, setWordingBullets] = React.useState<string[]>([]);
  const [verbageBullets, setVerbageBullets] = React.useState<string[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [divisor, setDivisor] = React.useState<number>(1);
  const [resumeData, setResumeData] = React.useState<ResumeContents>({
    contents: "",
  });
  const [numPages, setNumPages] = React.useState<number>(1);

  const updateRedirectUri = (uri: string) => {
    dispatch({
      type: "UPDATE_URI",
      payload: uri,
    });

    if (uri != "/") {
      navigate("/signup");
    }
  };

  const { height, width } = useWindowDimensions();

  React.useEffect(() => {
    setDivisor(GetWidthDivisor(width));
  }, [width]);

  async function queryUpdates() {
    const apiEndpoint =
       "/api/updates?file=" + user.resume_uuid;

    if (isLoading) {
      return;
    }

    const headers: [string, string][] = [];

    if (isAuthenticated) {
      headers.push(["Authorization", `Bearer ${token}`]);
    }

    headers.push(["Content-Type", "application/json"]);

    try {
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      if (response.ok) {
        const responseData = await response.json();

        setCompleted(responseData.completed);
        setUpdatesText(responseData.updates_text);
        setUpdatesTime(responseData.updates_time);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  const updateUserScore = (score: UserScore, uuid: string) => {
    const updatedUser: User = {
      ...userInfo, // create a new object with all existing properties of the user object
      score: score, // overwrite the name property with the new value
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function getResume() {
    if (isLoading) {
      return;
    }

    if (user.resume_uuid != "") {
      const apiEndpoint =
         "/api/resume?file=" + user.resume_uuid;

      let headers = {};

      if (isAuthenticated) {
        headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      try {
        const response = await fetch(apiEndpoint, {
          method: "GET",
          headers: headers,
        });

        if (response.ok) {
          const responseData = await response.json();

          const contents = responseData.contents;

          setResumeData({ contents });
          setNumPages(responseData.num_pages);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
        navigate("/");
      }
    } else {
      navigate("/resumes");
    }
  }

  async function showScore(uuid: string) {
    const apiEndpoint =  "/api/score?file=" + uuid;

    const headers: [string, string][] = [];

    if (isAuthenticated) {
      headers.push(["Authorization", `Bearer ${token}`]);
    }

    headers.push(["Content-Type", "application/json"]);

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      if (response.ok) {
        const responseData = await response.json();
        const scores = responseData.message as UserScore;
        const uuid = responseData.uuid as string;
        updateUserScore(scores, uuid);
        setNumbersBullets(responseData.message.numbers_bullets);
        setSkillsBullets(responseData.message.skills_bullets);
        setWordingBullets(responseData.message.wording_bullets);
        setVerbageBullets(responseData.message.verbage_bullets);
      } else {
        console.error("Error:", response.statusText);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      if (!completed && !isLoading) {
        queryUpdates();
        getResume();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [completed, isLoading]);

  React.useEffect(() => {
    if (user.resume_uuid != "" && !isLoading) {
      getResume();
    } else if (!isLoading) {
      navigate("/resumes");
    }
  }, [user.resume_uuid, isLoading]);

  React.useEffect(() => {
    if (completed && !isLoading) {
      showScore(user.resume_uuid);
      getResume();
    }
  }, [completed, isLoading]);

  React.useEffect(() => {
    setIsQuestionnaireOverlayOpen(false);
  }, [userInfo.resume_uuid]);

  async function getAccountInfo() {
    if (isLoading) {
      return;
    }

    const apiEndpoint =  "/api/accountinfo";

    let headers = {};

    if (!isAuthenticated) {
      setResumeCredits(1);
    } else {
      headers = {
        Authorization: `Bearer ${token}`,
      };

      try {
        // Make a POST request using fetch
        const response = await fetch(apiEndpoint, {
          method: "GET",
          headers: headers,
        });

        if (response.ok) {
          const responseData = await response.json();
          setResumeCredits(responseData.resume_credits);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("An error occured.");
      }
    }
  }

  React.useEffect(() => {
    if (!isLoading) {
      getAccountInfo();
    }
  }, [isLoading]);

  return (
    <div>
      <header
        className={`header dark-panel visible sticky top-0 z-20 mx-auto flex flex-row items-center justify-between p-2 lg:hidden lg:p-4 ${
          isQuestionnaireOverlayOpen || isTemplatesOverlayOpen
            ? "hidden"
            : "sticky"
        }`}
      >
        <img src={logo} className="h-auto w-1/2" alt="logo" />

        <div>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-white hover:text-gray-300 focus:outline-none"
          >
            <svg
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="h-12 w-12"
            >
              {isOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </header>
      <div className="dark-panel flex min-h-screen max-w-full flex-row">
        {isTemplatesOverlayOpen && (
          <div className="overlay z-10">
            <div className="questionnaire-shadow flex w-4/5 flex-col rounded-md bg-white py-[20px]">
              <button
                className="ml-auto mr-6 mt-4"
                onClick={() => setIsTemplatesOverlayOpen(false)}
              >
                <img className="industry-img scale-20" src={close} />
              </button>
              <div>
                <TemplatesDisplay
                  callback={() => {
                    setIsTemplatesOverlayOpen(false);
                    setIsQuestionnaireOverlayOpen(true);
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {isQuestionnaireOverlayOpen && (
          <div className="overlay z-10">
            <div className="questionnaire-shadow flex w-4/5 flex-col rounded-md bg-white py-[20px]">
              <button
                className="ml-auto mr-6 mt-4"
                onClick={() => setIsQuestionnaireOverlayOpen(false)}
              >
                <img className="industry-img scale-20" src={close} />
              </button>
              <div className="overflow-y-scroll">
                <Questionnaire
                  callback={() => {
                    setIsQuestionnaireOverlayOpen(false);
                    getResume();
                    setCompleted(false);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col pt-10 md:flex-row">
          <div className="hidden md:block xl:w-2/5 xl:min-w-[40vw]">
            <div
              className={`flex flex-col rounded-xl bg-[#1E1E1E] p-4 pt-2 xl:fixed 2xl:ml-20`}
            >
              <ResumeDisplay
                resumeData={resumeData}
                numPages={numPages}
                width={width / divisor}
              />
            </div>
          </div>
          <div className="mx-4 ml-auto flex w-full flex-col md:w-1/2 lg:w-2/5">
            <div className="flex w-full">
              <div className="mb-4 w-full flex-col rounded-2xl bg-[#1E1E1E] py-4 pl-2 pr-4 md:mb-0 md:pl-0 lg:pl-10">
                <ScoreCard
                  id="skills"
                  hits={user.score.total_skills}
                  total={completed ? user.score.total : 0}
                  text="Specific Skills"
                  color="#ff8800"
                  tooltip_text="Specific technical and soft skills should be mentioned throughout the main body of your resume"
                />
                <ScoreCard
                  id="numbers"
                  hits={user.score.total_numbers}
                  total={completed ? user.score.total : 0}
                  text="Quantified Impact"
                  color="#00b8e4"
                  tooltip_text="Each of your bullet points should include a specific numerical impact, where possible"
                />
                <ScoreCard
                  id="verbs"
                  hits={user.score.total_verbage}
                  total={completed ? user.score.total : 0}
                  text="Powerful Verbs"
                  color="#ff8800"
                  tooltip_text="Each bullet point should start with a powerful action verb like 'built' or 'designed'"
                />
                <ScoreCard
                  id="wording"
                  hits={user.score.total_wording}
                  total={completed ? user.score.total : 0}
                  text="Eloquent Language"
                  color="#00b8e4"
                  tooltip_text="The overall English writing quality of your resume"
                />
                <ScoreCard
                  id="total"
                  hits={
                    user.score.total_numbers +
                    user.score.total_wording +
                    user.score.total_verbage +
                    user.score.total_skills
                  }
                  total={completed ? user.score.total * 4 : 0}
                  text="Total Score"
                  color="#ff8800"
                  tooltip_text="Your total score"
                />
              </div>
            </div>
            <div className={`flex w-full flex-row pt-4`}>
              <div className="h-[50vh] max-h-[50vh] w-full overflow-y-scroll rounded-2xl  bg-[#1E1E1E]">
                {completed && (
                  <div className="pt-2">
                    <ScoreAccordion
                      heading={"Specific Skills"}
                      subheading="The number of bullet points identifying specific software engineering skills used."
                      improvement="complete our Interactive Resume Workshop using the button below"
                      bullets={skillsBullets === undefined ? [] : skillsBullets}
                    />
                    <ScoreAccordion
                      heading={"Quantified Impact"}
                      subheading="The number of bullet points identifying specific numerical impact."
                      improvement="complete our Interactive Resume Workshop using the button below"
                      bullets={
                        numbersBullets === undefined ? [] : numbersBullets
                      }
                    />
                    <ScoreAccordion
                      heading={"Powerful Verbs"}
                      subheading="The number of bullet points starting with a strong action verb."
                      improvement="generate a new resume based on this one, through the workshop or job tailoring section"
                      bullets={
                        verbageBullets === undefined ? [] : verbageBullets
                      }
                    />
                    <ScoreAccordion
                      heading={"Eloquent Language"}
                      subheading="The number of bullet points written in professional and correct English."
                      improvement="generate a new resume based on this one, through the workshop or job tailoring section"
                      bullets={
                        wordingBullets === undefined ? [] : wordingBullets
                      }
                    />
                  </div>
                )}
                {!completed && (
                  <div className="w-full pt-2">
                    {updatesText.map((text, index) => {
                      let time = 0;
                      const maybe_time = updatesTime.at(index);

                      if (maybe_time !== undefined) {
                        time = maybe_time;
                      }

                      const date = new Date(time * 1000);

                      // Extract hours, minutes, and seconds
                      const hours = date.getHours().toString().padStart(2, "0");
                      const minutes = date
                        .getMinutes()
                        .toString()
                        .padStart(2, "0");
                      const seconds = date
                        .getSeconds()
                        .toString()
                        .padStart(2, "0");

                      // Format the result as HH:MM:SS
                      const datetime = `${hours}:${minutes}:${seconds}`;

                      return (
                        <p
                          key={index}
                          className="light-text pl-4 pt-1 font-code"
                        >
                          [{datetime}] &gt; {text}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="block scale-75 md:hidden md:scale-90">
            <div className="flex flex-col pt-2">
              <ResumeDisplay
                resumeData={resumeData}
                numPages={numPages}
                width={width}
              />
            </div>
          </div>
          <button
            className={`${
              isQuestionnaireOverlayOpen || isTemplatesOverlayOpen
                ? "hidden"
                : "absolute"
            }`}
            onClick={() => {
              if (user.score.num_questions == 0) {
                toast.info("No questions from us. Good job!");
                return;
              }

              if (resumeCredits == 0) {
                toast.error(
                  "Insufficient resume credits (consider enjuggernaut+)."
                );
                return;
              }

              if (!completed) {
                toast.info("Please wait for analysis to complete...");
              }

              if (!isAuthenticated && !isLoading) {
                updateRedirectUri("/score?questionnaire=true");
              } else {
                updateRedirectUri("/");
                setIsTemplatesOverlayOpen(true);
              }
            }}
          >
            <div className="jobs fixed bottom-5 right-24 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-[#00b8e4] shadow-lg lg:right-48">
              <img src={workshop} />
            </div>
          </button>
          <button
            onClick={() => navigate("/jobs")}
            className={`${
              isQuestionnaireOverlayOpen || isTemplatesOverlayOpen
                ? "hidden"
                : "absolute"
            }`}
          >
            <div className="tailor fixed bottom-5 right-4 z-50 flex h-16 w-16 items-center justify-center rounded-full bg-[#ff8800] shadow-lg lg:right-28">
              <img src={tailor} />
            </div>
          </button>
          <Tooltip anchorSelect=".jobs" className="font-code" place="top">
            Interactive Resume Workshop
          </Tooltip>
          <Tooltip anchorSelect=".tailor" className="font-code" place="top">
            Tailor to Job Description
          </Tooltip>
        </div>
        <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)}>
          <Sidebar minimized={true} drawer={true} />
        </Drawer>
        <Sidebar minimized={true} drawer={false} />
      </div>
    </div>
  );
}
